/**
 * Ask Agent Button
 * =================
 * Small, unobtrusive button that connects any data point to the Agent Hub
 * Enables "one click to agent" from any metric or chart
 */

import React from 'react';
import { useLocation } from 'wouter';
import { Bot, MessageCircle } from 'lucide-react';
import { getQuestionForMetric, buildAgentHubUrl, type MetricContext } from '../lib/ask-agent-questions';

interface AskAgentButtonProps {
  /** The metric type (revenue, stockout, wos, etc.) */
  metric: string;
  /** Current value of the metric (for context in question) */
  value?: number | string;
  /** Percentage change (YoY or WoW) */
  delta?: number;
  /** Fiscal week */
  week?: string;
  /** Retailer name or ID */
  retailer?: string;
  retailerId?: string;
  /** Optional SKU for SKU-level context */
  sku?: string;
  /** Visual variant */
  variant?: 'icon' | 'text' | 'chip';
  /** Size */
  size?: 'sm' | 'md';
  /** Additional CSS classes */
  className?: string;
  /** Tooltip text */
  tooltip?: string;
}

export default function AskAgentButton({
  metric,
  value,
  delta,
  week,
  retailer,
  retailerId,
  sku,
  variant = 'icon',
  size = 'sm',
  className = '',
  tooltip = 'Ask AI Agent'
}: AskAgentButtonProps) {
  const [, setLocation] = useLocation();
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers
    
    const context: Partial<MetricContext> = {
      value,
      delta,
      week,
      retailer,
      retailerId,
      sku
    };
    
    const params = getQuestionForMetric(metric, context);
    const url = buildAgentHubUrl(params);
    
    console.log('[AskAgentButton] Navigating to:', url);
    setLocation(url);
  };
  
  // Size classes
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8'
  };
  
  const iconSize = size === 'sm' ? 14 : 18;
  
  // Render based on variant
  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`
          ${sizeClasses[size]}
          flex items-center justify-center
          rounded-full
          bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500
          text-white
          shadow-md shadow-purple-500/30
          border border-purple-400/50
          transition-all duration-200
          hover:scale-115 hover:shadow-lg hover:shadow-purple-500/40
          hover:from-purple-600 hover:via-blue-600 hover:to-cyan-600
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
          ${className}
        `}
        title={tooltip}
        aria-label={tooltip}
      >
        <Bot size={iconSize} strokeWidth={2.5} />
      </button>
    );
  }
  
  if (variant === 'chip') {
    return (
      <button
        onClick={handleClick}
        className={`
          inline-flex items-center gap-1.5
          px-2.5 py-1
          rounded-full
          bg-gradient-to-r from-purple-500 via-blue-500 to-cyan-500
          text-white
          text-xs font-semibold
          shadow-md shadow-purple-500/30
          border border-purple-400/50
          transition-all duration-200
          hover:shadow-lg hover:shadow-purple-500/40
          hover:from-purple-600 hover:via-blue-600 hover:to-cyan-600
          focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
          ${className}
        `}
        title={tooltip}
      >
        <Bot size={12} strokeWidth={2.5} />
        <span>Ask AI</span>
      </button>
    );
  }
  
  // Text variant
  return (
    <button
      onClick={handleClick}
      className={`
        inline-flex items-center gap-1
        bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-500 bg-clip-text text-transparent
        text-xs font-semibold
        hover:from-purple-700 hover:via-blue-600 hover:to-cyan-600
        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
        ${className}
      `}
      title={tooltip}
    >
      <MessageCircle size={12} className="text-purple-500" />
      <span>Ask Agent</span>
    </button>
  );
}

